import {
    ApartmentOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    ClockCircleOutlined,
    CloseCircleFilled,
    FlagFilled,
    PauseCircleFilled,
    PrinterFilled,
} from '@ant-design/icons'
import { Alert, CollapseProps, Typography } from 'antd'

const { Title } = Typography

export const helpItems: CollapseProps['items'] = [
    {
        key: '1',
        label: <b>Обзор раздела</b>,
        children: (
            <>
                <p>
                    Раздел «Отгрузки» — рабочий список заказов, перенесённых из расчётов. Каждая
                    строка таблицы — одна отгрузка; внутри может быть несколько видов (позиций).
                </p>
                <p>
                    Основной жизненный цикл: создание расчёта → редактирование → перенос в отгрузки
                    → запуск отгрузки → подготовка → спуск в печать.
                </p>
                <Alert
                    showIcon
                    style={{ marginTop: 8 }}
                    message='Запуск отгрузки выполняйте, когда вы уверены в параметрах заказа: после запуска часть полей будет заблокирована для редактирования.'
                    type='warning'
                />
                <p>
                    Создание отгрузок выполняется в разделе «Расчёты» (кнопка «Перенести в
                    отгрузки»). Подробнее о создании отгрузок — в разделе «Расчёты» → «Перенос
                    расчетов в отгрузки».
                </p>
            </>
        ),
    },
    {
        key: '2',
        label: <b>Просмотр и фильтрация</b>,
        children: (
            <>
                <Title level={3}>Фильтры</Title>
                <p>
                    В шапке можно отфильтровать отгрузки по заказчику, наименованию вида и месяцу
                    создания, а также по дополнительным полям: номер отгрузки, материалы, номер
                    штампа, ширина роля и дата поставки.
                </p>
                <p>Кнопка «Сброс» сбрасывает все активные фильтры.</p>
                <Title level={3}>Статистика</Title>
                <p>
                    В строке пагинации отображается оборот, прибыль и количество отгрузок за период.
                </p>
                <Title level={3}>Выбор строк</Title>
                <p>
                    Чекбоксы слева позволяют выбрать несколько отгрузок. При выборе доступна
                    групповая операция «Повторить заказ» — создание нового расчёта на основе
                    выбранных отгрузок.
                </p>
                <Title level={3}>Просмотр позиций</Title>
                <p>
                    Клик по кнопке с + слева от строки показывает позиции отгрузки (виды):
                    наименование, тираж и комментарий.
                </p>
            </>
        ),
    },
    {
        key: '3',
        label: <b>Редактирование отгрузки</b>,
        children: (
            <>
                <p>
                    Большинство полей редактируются кликом по ячейке: дата поставки, краски, лаки,
                    станки, намотка, цена и другие параметры. При изменении значения отгрузка
                    автоматически пересчитывается.
                </p>
                <Title level={3}>Позиции (виды)</Title>
                <p>Позиции редактируются в раскрытой строке: наименование, тираж, комментарий.</p>
                <Title level={3}>Ограничения</Title>
                <ul>
                    <li>
                        Если отгрузка уже отправлена в печать, редактирование строки заблокировано.
                    </li>
                    <li>Часть полей дополнительно блокируется при активной задаче «Заказ форм».</li>
                </ul>
                <Alert
                    showIcon
                    style={{ marginTop: 8 }}
                    message='После попадания в печать изменения параметров невозможны — вносите правки до отправки в печать.'
                    type='info'
                />
            </>
        ),
    },
    {
        key: '4',
        label: <b>Материалы</b>,
        children: (
            <>
                <p>Клик по колонке «Материалы» открывает модальное окно конфигурации материалов.</p>
                <Title level={3}>Действия с материалами</Title>
                <p>Для каждого типа материала указываются ширины с количеством и действием:</p>
                <ul>
                    <li>
                        <b>Зарезервировать</b> — материал закрепляется за заказом из остатков.
                    </li>
                    <li>
                        <b>Заказать</b> — создаётся заявка оператору на заказ материала.
                    </li>
                </ul>
                <p>
                    Справа вверху каждого блока указано, сколько уже сконфигурировано и сколько
                    требуется для тиража.
                </p>
                <p>
                    Конфигурацию можно задать при создании (форма переноса из расчётов) или изменить
                    позже на странице отгрузок.
                </p>
                <p>
                    <b>Запуск отгрузки</b> выполняет сконфигурированные действия: резервирование,
                    заявки на заказ.
                </p>
            </>
        ),
    },
    {
        key: '5',
        label: <b>Запуск отгрузки</b>,
        children: (
            <>
                <p>
                    Синяя кнопка Play (▶) в колонке номера открывает меню с действиями по отгрузке.
                </p>
                <Title level={3}>Пункты меню</Title>
                <ul>
                    <li>
                        <b>Запустить отгрузку</b> — полный запуск: обработка материалов, создание
                        задач и прочие действия, описанные ниже.
                    </li>
                    <li>
                        <b>Создать ТОЛЬКО заявку на заказ материалов</b> — создаёт заявки в
                        снабжение без полного запуска отгрузки.
                    </li>
                </ul>
                <Title level={3}>Что происходит при запуске</Title>
                <ul>
                    <li>Обрабатываются сконфигурированные материалы (резерв, заявки, разрез).</li>
                    <li>
                        Создаются задачи на дизайнера: отрисовка бланка, заказ форм, цифровой спуск
                        (для Durst) и другие — в зависимости от параметров отгрузки.
                    </li>
                    <li>
                        Если у штампа ещё нет изображения (чертежа), создаётся задача «Отрисовка
                        штампа». Задача «Заказ форм» в этом случае ожидает её завершения.
                    </li>
                </ul>
                <Title level={3}>Заказ штампа</Title>
                <p>
                    Заявка на заказ штампа создаётся на сервере автоматически, не на странице
                    отгрузок. Сценарий зависит от состояния штампа:
                </p>
                <ul>
                    <li>
                        <b>Штамп без чертежа</b> — при запуске создаётся задача «Отрисовка штампа».
                        Заявка на заказ штампа появится после того, как дизайнер завершит эту
                        задачу.
                    </li>
                    <li>
                        <b>Перезаказ штампа</b> — чертёж уже есть, но остаток штампа на складе равен
                        нулю. Заявка на заказ штампа создаётся сразу при запуске отгрузки.
                    </li>
                    <li>
                        <b>Штамп в наличии</b> — остаток больше нуля. Заявка на заказ штампа при
                        запуске не создаётся.
                    </li>
                </ul>
                <Title level={3}>Ограничения</Title>
                <p>
                    Кнопка недоступна, если отгрузка уже запущена (есть бланк или задача отрисовки и
                    материалы обработаны).
                </p>
                <Alert
                    showIcon
                    style={{ marginTop: 8 }}
                    message='Запускайте отгрузку только когда уверены в заказе: после запуска часть полей заблокируется для редактирования, а сконфигурированные материалы будут обработаны (резерв, заявки, разрез).'
                    type='warning'
                />
            </>
        ),
    },
    {
        key: '6',
        label: <b>Статусы подготовки</b>,
        children: (
            <>
                <p>
                    В колонке «Статус» отображаются иконки подготовки и печати. При наведении на
                    иконку подготовки открывается Popover с деталями.
                </p>
                <Title level={3}>Задачи подготовки</Title>
                <ul>
                    <li>Отрисовка бланка</li>
                    <li>Отрисовка штампа</li>
                    <li>Заказ форм</li>
                    <li>Перезаказ форм</li>
                    <li>Разрез материала</li>
                    <li>Цифровой спуск</li>
                </ul>
                <Title level={3}>Статусы задач</Title>
                <p>
                    <PauseCircleFilled style={{ color: '#fadb14', fontSize: 16 }} /> Новая
                    <br />
                    <ClockCircleFilled style={{ color: '#1677ff', fontSize: 16 }} /> В работе
                    <br />
                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} /> Завершена
                </p>
                <p>
                    В Popover отдельно показаны связанные задачи, заказы форм и оборудование
                    (штамп, материалы).
                </p>
                <p>Если бланк готов без задачи — показывается «Бланк — Готов».</p>
            </>
        ),
    },
    {
        key: '7',
        label: <b>Отправка в печать</b>,
        children: (
            <>
                <p>
                    Кнопка с иконкой принтера в колонке действий справа —{' '}
                    <b>«Отправить в печать»</b>. Создаёт задания печати и перемотки.
                </p>
                <p>Кнопка недоступна, если отгрузка уже в очереди печати.</p>
                <Title level={3}>Статусы печати</Title>
                <p>
                    <ApartmentOutlined style={{ color: '#2f54eb', fontSize: 16 }} /> В распределении
                    <br />
                    <ClockCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} /> В очереди на
                    станке
                    <br />
                    <PrinterFilled style={{ color: '#eb2f96', fontSize: 16 }} /> В работе
                    <br />
                    <FlagFilled style={{ color: '#52c41a', fontSize: 16 }} /> Завершено
                    <br />
                    <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} /> Отменено
                </p>
                <p>После отправки в печать редактирование отгрузки блокируется.</p>
            </>
        ),
    },
    {
        key: '8',
        label: <b>Дополнительные действия</b>,
        children: (
            <>
                <Title level={3}>Скачать бланк</Title>
                <p>Кнопка в колонке действий загружает клиентский бланк отгрузки.</p>
                <Title level={3}>Повторить заказ</Title>
                <p>
                    При выборе строк через чекбоксы доступна групповая операция «Повторить заказ» —
                    создание нового расчёта на основе выбранных отгрузок.
                </p>
                <Title level={3}>Доп. информация</Title>
                <p>
                    Popover в колонке «Доп.» показывает свойства отгрузки: упаковку, паллету,
                    менеджера, дизайнера, комментарий и т.д.
                </p>
                <p>
                    Свойство «Печать с куратором» можно менять прямо в popover чекбоксом — пока
                    отгрузка не отправлена в печать. Остальные свойства только для просмотра.
                </p>
                <Title level={3}>Печать с куратором</Title>
                <p>
                    Если у отгрузки включена «Печать с куратором», в Плане флексо печати и на
                    странице Станков в колонке «К» выводится тег «К» — печатники видят, что печать
                    нужно проводить с куратором. При наведении на тег видно, какой куратор
                    обрабатывал отгрузку: кто согласовал или отклонил задание (и когда), а если
                    решения ещё нет — кому назначена задача «Согласование задания».
                </p>
                <Title level={3}>Управление столбцами</Title>
                <p>
                    Через встроенный менеджер колонок таблицы можно скрывать и показывать столбцы.
                </p>
            </>
        ),
    },
]
